import React, {useState} from 'react'
import {
  AccordionCard,
  AccordionHeader,
  AccordionHeaderLeft,
  AccordionHeaderRight,
  ChevronIcon,
  DeleteButton,
  AccordionBody,
} from './Accordion.styles'

function Chevron() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M4 6.5l5 5 5-5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M3 5h12M7 5V3h4v2M6 5l1 10h4l1-10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Accordion — supports both controlled and uncontrolled modes.
 *
 * ── Uncontrolled (default) ────────────────────────────────────────────────────
 * @param {boolean} defaultOpen   - initial open state (default: false)
 *
 * ── Controlled ────────────────────────────────────────────────────────────────
 * @param {boolean}  isOpen       - controlled open state
 * @param {Function} onToggle     - called when header or any toggle trigger fires
 *
 * Controlled mode takes precedence when `isOpen` is provided.
 * This lets parent components (e.g. AccountRow) drive open state so that
 * external buttons (Manage) can expand the accordion programmatically.
 *
 * ── Other props ───────────────────────────────────────────────────────────────
 * @param {ReactNode} badge
 * @param {boolean}   disabled
 * @param {boolean}   canDelete
 * @param {Function}  onDelete
 * @param {string}    width
 * @param {'sm'|'md'|'lg'} size
 * @param {boolean}   noBorder
 * @param {boolean}   noShadow
 * @param {boolean}   hideChevron
 * @param {boolean}   noTopBorder
 */
export function Accordion({
  badge,
  children,
  // uncontrolled
  defaultOpen = false,
  // controlled
  isOpen: controlledIsOpen,
  onToggle,
  // behaviour
  disabled = false,
  canDelete = false,
  onDelete,
  // layout
  width,
  size = 'md',
  // style overrides
  noBorder = false,
  noShadow = false,
  hideChevron = false,
  noTopBorder = false,
}) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen)

  // If isOpen is provided externally, use controlled mode
  const isControlled = controlledIsOpen !== undefined
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen

  const handleToggle = () => {
    if (isControlled) {
      onToggle?.()
    } else {
      setUncontrolledIsOpen(o => !o)
    }
  }

  return (
    <AccordionCard
      width={width}
      noBorder={noBorder}
      noShadow={noShadow}
      isOpen={isOpen}
    >
      <AccordionHeader
        role="button"
        tabIndex={disabled ? -1 : 0}
        size={size}
        aria-expanded={isOpen}
        onClick={disabled ? undefined : handleToggle}
        onKeyDown={
          disabled
            ? undefined
            : e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleToggle()
                }
              }
        }
        isOpen={isOpen}
      >
        <AccordionHeaderLeft>{badge}</AccordionHeaderLeft>

        <AccordionHeaderRight>
          {canDelete && (
            <DeleteButton
              type="button"
              aria-label="Delete"
              onClick={e => {
                e.stopPropagation()
                onDelete?.()
              }}
            >
              <TrashIcon />
            </DeleteButton>
          )}

          <ChevronIcon isOpen={isOpen} hideChevron={hideChevron}>
            <Chevron />
          </ChevronIcon>
        </AccordionHeaderRight>
      </AccordionHeader>

      {isOpen && (
        <AccordionBody size={size} noTopBorder={noTopBorder} isOpen={isOpen}>
          {children}
        </AccordionBody>
      )}
    </AccordionCard>
  )
}
